import get from 'lodash/get';

import { getCookie } from 'Helpers/Cookie/CookieUtils';
import struct from 'Redux/analytics/structEvent';
import { persistedUserId, ageGatingPassed } from 'Redux/analytics/persistedState';
import { basketAdd, basketAddMany, basketRemove } from 'Redux/analytics/eventMappers/basket';

/**
 * Redux action -> native Snowplow event.
 * The `event` key picks the Snowplow method; everything else is that method's data.
 *
 *   struct       -> trackStructEvent  (everything below except the last three)
 *   pageView     -> trackPageView
 *   transaction  -> addTrans + addItem xN + trackTrans
 *
 * THE AGREED TABLE. This is what the analytics team support and expect
 *   se_category                       se_action        se_label            se_property     se_value
 *   ---------------------------------------------------------------------------------------------------
 *   AutoSearch                        Search           Auto Search Reason  Auto Search Q   nbHits
 *   AutoSearch                        More             Auto Search Reason  Auto Search Q   -
 *   AutoSearch                        Result           Auto Search Reason  Auto Search Q   (see note)
 *   basket                            add              diff[0]             cartBeforeStr   -
 *   basket                            remove           diff[0]             cartBeforeStr   -
 *   Click                             Click Section    Click Value         -               -
 *   Auth Event                        action           value               -               -
 *   Login                             User Id          -                   -               -
 *   loginView                         Modal            -                   -               -
 *   Logout                            User Id          -                   -               -
 *   order created                     Order Id         -                   -               -
 *   Signup                            User Id          -                   -               -
 *   split_test                        Split Test Name  Split Test Group    ag (age gate)   -
 *   voucher                           add              Voucher.data._id    1               -
 *   voucher                           add              Voucher.voucherText 0               -
 *   Wishlist                          Add | Remove     Product Id          -               -
 *   addTrans                          Order Id         Affiliate Source    Order Total     Order Tax
 *   addItem                           Order Id         product.sku         Product Name    product.price
 *   trackTrans                        -                -                   -               -
 *
 */

/* ===========================================================================
 * Core Events
 * ===========================================================================
 *
 * Everything below is part of the agreed table and is supported by analytics.
 * ======================================================================== */

const pageView = () => ({
  event: 'pageView',
  userId: persistedUserId(),
});

const autoSearch = action => struct({
  category: 'AutoSearch',
  action: 'Search',
  label: get(action, 'payload.reason'),
  property: get(action, 'payload.query'),
  value: get(action, 'payload.nbHits'),
});


const autoSearchMore = action => struct({
  category: 'AutoSearch',
  action: 'More',
  label: get(action, 'payload.reason'),
  property: get(action, 'payload.query'),
});

const autoSearchResult = action => struct({
  category: 'AutoSearch',
  action: 'Result',
  label: get(action, 'payload.reason'),
  property: get(action, 'payload.query'),
});


const clickInteraction = action => struct({
  category: 'Click',
  action: get(action, 'payload.section'),
  label: get(action, 'payload.value'),
});

/*
* this remains in its broken state, this wil addressed as email subscribe now has a 3rd state
* Optin in required, analytics will need to know this event never fired
*/

/**
 * The Login tag sets the tracker's user id before sending, so every later event
 * on the page inherits it:
 */
const login = () => struct({
  category: 'Login',
  action: persistedUserId(),
  setUserId: persistedUserId(),
});

const signUp = () => struct({
  category: 'Signup',
  action: persistedUserId(),
});


const logout = () => struct({
  category: 'Logout',
  action: persistedUserId(),
});

const showSignIn = () => struct({
  category: 'loginView',
  action: 'signin',
});
const showRegister = () => struct({
  category: 'loginView',
  action: 'register',
});

const authLogging = action => struct({
  category: 'Auth Event',
  action: get(action, 'payload.action'),
  label: get(action, 'payload.value'),
});


const orderCreated = action => struct({
  category: 'order created',
  action: get(action, 'payload.transactionId'),
});

/**
 * The revenue event: addTrans + addItem per product + trackTrans.
 *
 * Dispatched by reportToSnowplow in ducks/receipt.js once the order is
 * confirmed. Field for field, this is the GTM tag it replaces, so the rows
 * stay one continuous series across the migration:
 *
 *   addTrans  transactionId, affiliate_source cookie, transactionTotal, transactionTax,
 *             transactionCurrency
 *   addItem   transactionId, sku, name, category, price, quantity, transactionCurrency
 *             (per product)
 *   trackTrans
 *
 * Three of those are easy to get subtly wrong, so to be explicit:
 *
 *   - total is transactionTotal (order.report.total), NOT transactionRevenue.
 *     Both are on the payload and the tag used the former.
 *   - tax is transactionTax (summed item vat, via CheckoutUtils), NOT
 *     transactionTaxTotal (order.report.vat). Again both are on the payload.
 *   - affiliation is the `affiliate_source` COOKIE. It is NOT awinChannel:
 *     that is derived from AttributionUtils' localStorage record and is a
 *     different value. Nothing in this app writes affiliate_source, so it is
 *     set externally and may simply be absent.
 *
 */
const transaction = action => ({
  event: 'transaction',
  orderId: get(action, 'payload.transactionId', ''),
  affiliation: getCookie('affiliate_source') || '',
  total: get(action, 'payload.transactionTotal', 0),
  tax: get(action, 'payload.transactionTax', 0),
  currency: get(action, 'payload.transactionCurrency', ''),
  items: (get(action, 'payload.transactionProducts') || []).map(product => ({
    sku: get(product, 'sku', ''),
    name: get(product, 'name', ''),
    category: get(product, 'category', ''),
    price: get(product, 'price', 0),
    quantity: get(product, 'quantity', 1),
  })),
});

const splitTest = action => struct({
  category: 'split_test',
  action: get(action, 'payload.experimentName'),
  label: get(action, 'payload.variant'),
  property: ageGatingPassed(),
});

const voucherAdded = (action) => {
  const voucher = get(action, 'payload.voucher');
  if (!voucher) return null;
  const voucherId = get(voucher, '_id');
  return struct({
    category: 'voucher',
    action: 'add',
    label: voucherId || get(action, 'payload.voucherText'),
    property: voucherId ? 1 : 0,
  });
};


const wishlistInteraction = action => struct({
  category: 'Wishlist',
  action: get(action, 'payload.remove') ? 'Remove' : 'Add',
  label: get(action, 'payload.productId'),
});

/* ===========================================================================
 * Nice to have events
 * ===========================================================================
 *
 * Everything below is not part of the events that analytics are dependant on
 * however they are still being sent to snowplow. They are here for reference
 * with the intention to be used when analytics when they're ready to support them.
 * ========================================================================
*/

const outOfStock = action => struct({
  category: 'product',
  action: 'out_of_stock_viewed',
  property: get(action, 'payload.wrong_region') ? 'wrong_region' : undefined,
});

const changeLanguage = action => struct({
  category: 'localisation',
  action: 'language_changed',
  label: get(action, 'payload.from'),
  property: get(action, 'payload.to'),
});

const themeSwitch = (action) => {
  const theme = get(action, 'payload');
  return struct({
    category: 'preferences',
    action: 'theme_changed',
    property: typeof theme === 'string' ? theme : undefined,
  });
};

const notFound = () => struct({
  category: 'diagnostics',
  action: 'page_not_found',
});

// 604: a cart matched a themed mystery bundle. Likely won't be used but better to have the tracking in place
const themedUpsellEligible = action => struct({
  category: 'themed_upsell',
  action: get(action, 'payload.theme'),
  label: get(action, 'payload.variant'),
  property: get(action, 'payload.matchedItems', []).map(item => item._id).join(',').slice(0, 255) || undefined,
});


/**
 * Sent here but NOT in GTM — tell analytics, these are new rows:
 *
 *  basket/add on cart/ADD_ALL
 *      GTM has no basket tag on the AddMany trigger, so bulk adds
 *      (pick-and-mix, bundles) have never reached Snowplow. Now sent, one
 *      basket|add per added id. Expect basket|add volume to rise and to start
 *      reconciling with actual items added. See basketAddMany.
 *
 *  voucher/add — the whole category
 *      The GTM tag exists and looks healthy, but its map entry keys on
 *      'frontend/cart/ADD_VOUCHER' while the duck has dispatched
 *      'frontend/voucher/ADD_VOUCHER' since April 2019. redux-beacon matches the
 *      action type exactly, so the AddVoucher hit has never been pushed and that
 *      tag has never run. Every voucher|add row will be new. Analytics may well
 *      believe this event already works, so worth saying out loud rather than
 *      letting a dormant category suddenly populate.
 *
 * Open questions for analytics:
 *
 *  Login/Signup se_label has always been the constant 'false'
 *      {{Newsletter Signup}} reads the data layer key `newsletter`, which no
 *      dispatch site ever sets — the
  * `newsletter` is the data layer key {{Newsletter Signup}} reads, and it is never
 * dispatched on either action — the GTM mapper has supplied its own `false`
 * default since it was written, so se_label has always been the constant 'false'.
 * Reproduced here, default included.
 GTM mapper's own `false` default is what
 *      has been sent since the tag was written. Faithfully reproduced, so the
 *      column stays constant. The real flag exists but is thrown away at the
 *      dispatch site: ducks/auth.js newRegister receives `newsletter` and spends
 *      it on postEmail without putting it on the SIGNUP_SUCCESS payload. Fixing
 *      it properly means dispatching that flag, which is a duck change.
 *
 *  The tracker's uid on anonymous page views — SETTLED, do not change
 *      user_id means a known user, so it must never carry an anonymous id. Every
 *      path here uses persistedUserId(), which has no anon fallback, and
 *      snowplowTarget only calls setUserId for a non-empty string — so an
 *      anonymous visitor gets no uid at all. Use domain_session_id for those.
 *      Do not swap in State.getUserId(): it falls back to State.getAnonId().
 *
 *  Transaction amounts are in PENCE — CONFIRMED deliberate, leave alone
 *      report.total / report.vat / item.report.total are integer pence and the
 *      GTM tag passed them undivided, so a £29.99 order records 2999. Kept to
 *      hold the revenue series continuous across the migration. The Awin basket
 *      in ducks/receipt.js divides; the two disagree by design.
 *
 *  Transaction currency is now sent — the one deliberate divergence from GTM
 *      The GTM tag sent ''. At the data team's request this sends
 *      order.report.currencyCode, off the same `report` object as the amounts.
 */

export default {
  '@@router/LOCATION_CHANGE': pageView,

  // --- the agreed table ---
  'frontend/autosearch/AUTOSEARCH': autoSearch,
  'frontend/autosearch/AUTOSEARCH_MORE': autoSearchMore,
  'frontend/autosearch/AUTOSEARCH_RESULT': autoSearchResult,
  'frontend/cart/ADD_CART': basketAdd,
  'frontend/cart/ADD_ALL': basketAddMany, // we didn't formely support addMany in GTM
  'frontend/cart/REMOVE_CART': basketRemove,
  'frontend/click/CLICK_INTERACTION': clickInteraction,
  'frontend/auth/LOGIN_SUCCESS': login,
  'frontend/auth/SIGNUP_SUCCESS': signUp,
  'frontend/auth/LOGOUT': logout,
  'frontend/auth/SHOW_SIGN_IN': showSignIn,
  'frontend/auth/SHOW_REGISTER': showRegister,
  'frontend/checkout/CREATE_ORDER': orderCreated,
  'frontend/receipt/COMPLETE_ORDER': transaction,
  'frontend/split-test/SPLIT_TEST': splitTest,
  'frontend/voucher/ADD_VOUCHER': voucherAdded,
  'frontend/wishlist/WISHLIST_INTERACTION': wishlistInteraction,
  'frontend/auth/AUTH_LOG': authLogging,

  // --- Nice to have events ---
  'frontend/out-of-stock/OUT_OF_STOCK': outOfStock,
  'frontend/languages/CHANGE_LANGUAGE': changeLanguage,
  'frontend/site-theme/SWITCH_SITE_THEME': themeSwitch,
  'frontend/not-found/NOT_FOUND': notFound,
  'frontend/mystery/THEMED_UPSELL_ELIGIBLE': themedUpsellEligible,
};
